import { useEffect, useRef, useState, useMemo } from 'react';

import { useToggle, useScreenSize } from 'services/hooks';
import { totalSeconds } from 'services/utils';
import { useAppContext } from 'services/context';

import classNames from 'classnames';

import './style.css';
import { LANGUAGES } from 'constants';

export const MUTED_VALUE = 0.05;
export const LOW_VALUE = 0.5;

//define video container attribute - importnat for csvolumeSliders!
export const VOLUME_LEVELS = Object.freeze({
	MUTED: 'muted',
	LOW: 'low',
	HIGH: 'high',
});

export const leadingZeroFormatter = new Intl.NumberFormat(undefined, { minimumIntegerDigits: 2 });

let debounceTimeOut;

const VideoPlayer = ({ src, title = '', courseId }) => {
	const { Api } = useAppContext();

	const [paused, setPaused] = useState(true);
	const [theater, toggleTheater] = useToggle(false);
	const [miniPlayer, toggleMiniPlayer] = useToggle(false);
	const [fullScreen, toggleFullScreen] = useToggle(false);
	const [volumeLevel, setVolumeLevel] = useState(VOLUME_LEVELS.LOW);
	const [volume, setVolume] = useState(LOW_VALUE);
	const [currentTime, setCurrentTime] = useState(`0:${leadingZeroFormatter.format(0)}`);
	const [totalTime, setTotalTime] = useState(`0:${leadingZeroFormatter.format(0)}`);
	const [playPauseActive, togglePlayPauseActive] = useState(true);
	const [timeLine, setTimeLine] = useState(0);
	const [buffered, setBuffered] = useState(0);
	const [videoSpeed, setVideoSpeed] = useState(1);
	const [captionsVisible, setCaptionsVisible] = useState(false);
	const [captionsLanguageISO, setCaptionsLanguageISO] = useState('EN');
	const [captionsAvailableLanguages, setCaptionsAvailableLanguages] = useState([]);
	const [isPosterAvailable, setIsPosterAvailable] = useState(false); //If there is a poster (preview image) available for the video.
	const [isScrubbing, setIsScrubbing] = useState(false);

	const videoRef = useRef(null);
	const videoContainerRef = useRef(null);
	const timeLineContainerRef = useRef(null);

	const screenSize = useScreenSize();
	const TABLET_SIZE = process.env.REACT_APP_TABLET_SIZE;

	const className = classNames(
		'VideoPlayer',
		{ paused: paused },
		{ theater: theater },
		{ 'full-screen': fullScreen },
		{ 'mini-player': miniPlayer }
	);

	const playPauseClassNames = classNames({ play_pause_effect: true, play_pause_effect__active: playPauseActive });

	//Play Button Handler
	const _togglePlayVideo = () => {
		setPaused(prev => !prev);

		if (paused) {
			videoRef.current?.play();

			return;
		}
		videoRef.current?.pause();
	};

	//toggle browser full screen state
	const _toggleFullScreen = () => {
		if (document.fullscreenElement === null) {
			videoContainerRef.current?.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
	};

	//toggle mini player state
	const _toggleMiniPlayer = async () => {
		toggleMiniPlayer(prev => !prev);
	};

	//mute and unmute
	const _toggleMute = () => {
		if (volume <= MUTED_VALUE) {
			setVolume(0.5);
			return;
		}
		setVolume(0.0);
	};

	//handle volume change
	const _handleVolume = ({ target }) => {
		let value = target.value;
		if (value < MUTED_VALUE) {
			value = 0;
		}
		setVolume(value);
	};

	//change video speed
	const _handleVideoPlaySpeed = () => {
		let newVideoSpeed = videoSpeed === 2 ? 0.5 : videoSpeed + 0.25;
		setVideoSpeed(newVideoSpeed);
		videoRef.current.playbackRate = newVideoSpeed;
	};

	//duration
	const durationFormat = time => {
		const seconds = Math.floor(time % 60);
		const minutes = Math.floor(time / 60) % 60;
		const hours = Math.floor(time / 3600);
		if (hours === 0) {
			return `${minutes}:${leadingZeroFormatter.format(seconds)}`;
		}
		return `${hours}:${leadingZeroFormatter.format(minutes)}:${leadingZeroFormatter.format(seconds)}`;
	};

	//video on load
	const _handleVideoLoad = ({ target }) => {
		const time = durationFormat(target?.duration || 0);
		setTotalTime(time);
	};

	//timeline and video time
	const _handleTimeLineOnPointerDown = e => {
		setIsScrubbing(true);
		videoRef.current?.pause();
		_updateTimeline(e);
	};

	const _handleTimeLineOnPointerMove = e => {
		if (!isScrubbing) return;
		_updateTimeline(e);
	};

	const _handleTimeLineOnPointerUp = e => {
		_updateTimeline(e);
		setIsScrubbing(false);
		setPaused(false);
		videoRef.current?.play();
	};

	const _updateTimeline = e => {
		const parent = timeLineContainerRef.current.getBoundingClientRect();
		const percentage = Math.min(Math.max(0, e.clientX - parent.x), parent.width) / parent.width;
		const newTimeLine = percentage * videoRef.current.duration;
		videoRef.current.currentTime = newTimeLine;
	};

	//update video , progressbar and buffering
	const _handleVideoTimeUpdate = ({ target }) => {
		let currTime = target.currentTime;
		currTime = durationFormat(currTime || 0);

		const time = durationFormat(target?.duration || 0);
		const newCurrTime = ((100 * totalSeconds(currTime)) / totalSeconds(time)).toFixed(2);
		setCurrentTime(currTime);
		setTimeLine(newCurrTime);
	};

	//video buffering
	const _handleVideoBuffering = ({ target }) => {
		if (target.buffered.length <= 0) return;
		const bufferedTime = target.buffered.end(target.buffered.length - 1);
		let formattedBufferedTime = durationFormat(bufferedTime);
		formattedBufferedTime = ((100 * totalSeconds(formattedBufferedTime)) / totalSeconds(totalTime)).toFixed(2);
		setBuffered(formattedBufferedTime);
	};

	//captions section
	const _toggleCaptions = (iso, e) => {
		e.stopPropagation();
		setCaptionsVisible(!!iso);
		if (!iso) return;
		setCaptionsLanguageISO(iso);
	};

	//captions url
	const _captionsUrl = useMemo(() => {
		if (!src) return '';
		if (captionsAvailableLanguages?.length <= 0) return;
		if (typeof src !== 'string') return '';
		return `${src.substring(0, src.length - 4)}_subtitles_${captionsLanguageISO}.vtt`;
	}, [src, captionsLanguageISO, captionsAvailableLanguages]);

	//The poster url/src is the same as the video, only with a png at the end instead of mp4.
	const posterUrl = useMemo(() => {
		return src ? src?.replace(/\.[^./]+$/, '.png') : null;
	}, [src]);

	useEffect(() => {
		if (!courseId) return;
		(async () => {
			const languageIds = await Api.GetVideosSubtitlesLang(courseId);
			if (!languageIds) return;
			const _availableLanguages = LANGUAGES.filter(language => languageIds.includes(language.id));
			setCaptionsAvailableLanguages(_availableLanguages);
		})();
	}, [courseId]);

	// watch for fullscreenchange
	useEffect(() => {
		document.addEventListener('fullscreenchange', toggleFullScreen);
		return () => document.removeEventListener('fullscreenchange', toggleFullScreen);
	}, []);

	// watch for caption change
	useEffect(() => {
		if (!videoRef.current) return;
		const captions = videoRef.current.textTracks[0];
		if (!captions) return;
		captions.mode = 'hidden';
		captions.mode = captionsVisible ? 'showing' : 'hidden';
	}, [captionsVisible, videoRef, src]);

	//volume change icon handler
	useEffect(() => {
		if (!videoRef?.current) return;
		videoRef.current.volume = volume;
		if (volume < MUTED_VALUE) {
			setVolumeLevel(VOLUME_LEVELS.MUTED);
			videoRef.current.muted = true;
			return;
		}

		videoRef.current.muted = false;
		if (volume >= MUTED_VALUE && volume < LOW_VALUE) setVolumeLevel(VOLUME_LEVELS.LOW);
		else setVolumeLevel(VOLUME_LEVELS.HIGH);
	}, [volume]);

	//set volume on mobile to 100 to allow mobile native buttons control
	useEffect(() => {
		if (screenSize.width > TABLET_SIZE) return;
		setVolume(1);
	}, [screenSize.width]);

	//handle play and pause background effect
	useEffect(() => {
		clearTimeout(debounceTimeOut);
		togglePlayPauseActive(true);
		if (!paused) {
			debounceTimeOut = setTimeout(() => togglePlayPauseActive(false), 700);
		}
	}, [paused]);

	useEffect(() => {
		setPaused(true);
		setTimeLine(0);
		setBuffered(0);
		setVideoSpeed(1);
		setIsPosterAvailable(false);

		//Checks if a video poster (preview image) exists on the server.
		(async () => {
			if (!src || !src?.length || !posterUrl) return;

			const isAvailable = await Api.isVideoPosterAvailable(posterUrl);
			setIsPosterAvailable(isAvailable);
		})();
	}, [src]);

	const _renderLanguage = ({ description, id, iso, nativeDescription }) => {
		return (
			<div
				key={id}
				className={`iso ${captionsVisible && iso === captionsLanguageISO ? 'active' : ''}`}
				onClick={e => _toggleCaptions(iso, e)}
			>
				{nativeDescription}
			</div>
		);
	};

	if (!src) return null;

	return (
		<div className={className} ref={videoContainerRef} data-volume-level={volumeLevel}>
			<div className="video_controls-container">
				<div className="timeline-container" ref={timeLineContainerRef}>
					<div
						onPointerDown={_handleTimeLineOnPointerDown}
						onPointerMove={_handleTimeLineOnPointerMove}
						onPointerUp={_handleTimeLineOnPointerUp}
						className="timeline"
						style={{
							'--progress-position': timeLine,
							'--buffered-position': buffered,
						}}
					>
						<div className="thumb-indicator"></div>
					</div>
				</div>
				<div className="controls">
					<button className="play-pause-btn" onClick={_togglePlayVideo}>
						<SVGS name="play-icon" size="24" />
						<SVGS name="pause-icon" size="24" />
					</button>
					<div className="volume-container">
						<button className="mute-btn" onClick={_toggleMute}>
							<SVGS name="volume-high-icon" />
							<SVGS name="volume-low-icon" />
							<SVGS name="volume-muted-icon" />
						</button>
						<input className="volume-slider" type="range" min="0" max="1" step="0.01" value={volume} onChange={_handleVolume} />
					</div>
					<div className="duration-container">
						<div className="current-time">{currentTime}</div>
						<span>/</span>
						<div className="total-time">{totalTime}</div>
					</div>
					{captionsAvailableLanguages?.length > 0 && (
						<button className={`captions-btn ${captionsVisible ? 'captions-on' : ''}`} onClick={e => setCaptionsVisible(prev => !prev)}>
							<SVGS name="captions-btn" />
							<div className="languages">{captionsAvailableLanguages.map(_renderLanguage)}</div>
						</button>
					)}
					<button className="play-speed-btn" onClick={_handleVideoPlaySpeed}>
						{videoSpeed}x
					</button>
					<button className="mini-player-btn" onClick={_toggleMiniPlayer}>
						<SVGS name="mini-player-btn" />
					</button>
					<button className="theater-btn" onClick={toggleTheater}>
						<SVGS name="theater-tall-btn" />
						<SVGS name="theater-wide-btn" />
					</button>
					<button className="full-screen-btn" onClick={_toggleFullScreen}>
						<SVGS name="open" />
						<SVGS name="close" />
					</button>
				</div>
			</div>
			<div className="title_container">{title || ''}</div>
			<div className={playPauseClassNames}>
				<SVGS name="play-icon" size="24" />
				<SVGS name="pause-icon" size="24" />
			</div>
			<video
				crossOrigin="anonymous"
				preload="auto"
				controlsList="nodownload"
				poster={isPosterAvailable ? posterUrl : null} //Preview image.
				src={src}
				ref={videoRef}
				onTimeUpdate={_handleVideoTimeUpdate}
				onLoadedMetadata={_handleVideoLoad}
				onClick={_togglePlayVideo}
				onProgress={_handleVideoBuffering}
				onEnded={_togglePlayVideo}
			>
				{_captionsUrl && <track kind="captions" src={_captionsUrl} />}
			</video>
		</div>
	);
};

export default VideoPlayer;

export const SVGS = ({ name, size = 27 }) => {
	switch (name) {
		case 'open':
			return (
				<svg className="open" viewBox={`0 0 ${size} ${size}`}>
					<path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
				</svg>
			);
		case 'close':
			return (
				<svg className="close" viewBox={`0 0 ${size} ${size}`}>
					<path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
				</svg>
			);
		case 'play-icon':
			return (
				<svg className="play-icon" viewBox={`0 0 ${size} ${size}`}>
					<path fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z" />
				</svg>
			);
		case 'pause-icon':
			return (
				<svg className="pause-icon" viewBox={`0 0 ${size} ${size}`}>
					<path fill="currentColor" d="M14,19H18V5H14M6,19H10V5H6V19Z" />
				</svg>
			);
		case 'volume-high-icon':
			return (
				<svg className="volume-high-icon" viewBox={`0 0 ${size} ${size}`}>
					<path
						fill="currentColor"
						d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z"
					/>
				</svg>
			);

		case 'volume-low-icon':
			return (
				<svg className="volume-low-icon" viewBox="0 0 27 27">
					<path fill="currentColor" d="M5,9V15H9L14,20V4L9,9M18.5,12C18.5,10.23 17.5,8.71 16,7.97V16C17.5,15.29 18.5,13.76 18.5,12Z" />
				</svg>
			);
		case 'volume-muted-icon':
			return (
				<svg className="volume-muted-icon" viewBox="0 0 27 27">
					<path
						fill="currentColor"
						d="M12,4L9.91,6.09L12,8.18M4.27,3L3,4.27L7.73,9H3V15H7L12,20V13.27L16.25,17.53C15.58,18.04 14.83,18.46 14,18.7V20.77C15.38,20.45 16.63,19.82 17.68,18.96L19.73,21L21,19.73L12,10.73M19,12C19,12.94 18.8,13.82 18.46,14.64L19.97,16.15C20.62,14.91 21,13.5 21,12C21,7.72 18,4.14 14,3.23V5.29C16.89,6.15 19,8.83 19,12M16.5,12C16.5,10.23 15.5,8.71 14,7.97V10.18L16.45,12.63C16.5,12.43 16.5,12.21 16.5,12Z"
					/>
				</svg>
			);
		case 'captions-btn':
			return (
				<svg className="captions-btn">
					<path
						fill="currentColor"
						d="M18,11H16.5V10.5H14.5V13.5H16.5V13H18V14A1,1 0 0,1 17,15H14A1,1 0 0,1 13,14V10A1,1 0 0,1 14,9H17A1,1 0 0,1 18,10M11,11H9.5V10.5H7.5V13.5H9.5V13H11V14A1,1 0 0,1 10,15H7A1,1 0 0,1 6,14V10A1,1 0 0,1 7,9H10A1,1 0 0,1 11,10M19,4H5C3.89,4 3,4.89 3,6V18A2,2 0 0,0 5,20H19A2,2 0 0,0 21,18V6C21,4.89 20.1,4 19,4Z"
					/>
				</svg>
			);
		case 'mini-player-btn':
			return (
				<svg className="mini-player-btn" viewBox="0 0 27 27">
					<path
						fill="currentColor"
						d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7h9v6h-9z"
					/>
				</svg>
			);
		case 'theater-tall-btn':
			return (
				<svg className="theater-tall-btn" viewBox="0 0 27 27">
					<path fill="currentColor" d="M19 6H5c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H5V8h14v8z" />
				</svg>
			);
		case 'theater-wide-btn':
			return (
				<svg className="theater-wide-btn" viewBox="0 0 27 27">
					<path fill="currentColor" d="M19 7H5c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm0 8H5V9h14v6z" />
				</svg>
			);
		case 'full-screen-open':
			return (
				<svg className="full-screen-open" viewBox="0 0 27 27">
					<path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
				</svg>
			);
		case 'full-screen-close':
			return (
				<svg className="full-screen-close" viewBox="0 0 27 27">
					<path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
				</svg>
			);
		default:
			break;
	}
};
